// src/admin/pages/AdminPlans.jsx
import { useState, useEffect } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const DEFAULT_FEATURES = {
  maxReportsPerMonth:  5,
  maxSearchesPerDay:   5,
  canExportCSV:        false,
  canReceiveAlerts:    true,
  canViewReportImages: true,
  canBulkImport:       false,
};

const AdminPlans = () => {
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // "create" | "edit"
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/plans`, { headers: adminAuthHeader() });
      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      name: "", slug: "", description: "", color: "#4318FF",
      price: 0, billingCycle: "yearly", validityDays: 365, trialDays: 0,
      isActive: true, isDefault: false, sortOrder: plans.length + 1,
      features: { ...DEFAULT_FEATURES },
    });
    setMsg(""); setModal("create");
  };

  const openEdit = (plan) => {
    setForm({ ...plan, features: { ...DEFAULT_FEATURES, ...plan.features } });
    setMsg(""); setModal("edit");
  };

  const setF    = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setFeat = (key, val) => setForm(f => ({ ...f, features: { ...f.features, [key]: val } }));

  const handleSave = async () => {
    if (!form.name || !form.slug) { setMsg("❌ Name and slug are required"); return; }
    setSaving(true); setMsg("");
    try {
      const url    = modal === "create" ? `${API_BASE_URL}/api/plans` : `${API_BASE_URL}/api/plans/${form._id}`;
      const method = modal === "create" ? "POST" : "PUT";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg("✅ " + data.message);
        setTimeout(() => { setModal(null); setMsg(""); load(); }, 1000);
      } else { setMsg("❌ " + data.message); }
    } catch { setMsg("❌ Failed"); }
    setSaving(false);
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"? This cannot be undone.`)) return;
    try {
      const res  = await fetch(`${API_BASE_URL}/api/plans/${plan._id}`, {
        method: "DELETE", headers: adminAuthHeader(),
      });
      const data = await res.json();
      if (res.ok) { load(); } else { alert(data.message); }
    } catch { alert("Delete failed"); }
  };

  const handleSetDefault = async (plan) => {
    try {
      await fetch(`${API_BASE_URL}/api/plans/${plan._id}/set-default`, {
        method: "PUT", headers: adminAuthHeader(),
      });
      load();
    } catch { alert("Failed"); }
  };

  // ── Pause / Unpause plan ─────────────────────────────────────
  const handleToggleActive = async (plan) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans/${plan._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify({ isActive: !plan.isActive }),
      });
      if (res.ok) { load(); } else { alert("Failed to update plan"); }
    } catch { alert("Failed"); }
  };

  return (
    <AdminLayout>
      <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p className="admin-welcome">Subscription Management</p>
          <h2 className="admin-title">Subscription Plans</h2>
        </div>
        <button onClick={openCreate}
          style={{ padding: "10px 20px", background: "#4318FF", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          + Create New Plan
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#a3aed0" }}>Loading plans...</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, marginTop: 8 }}>
          {plans.map(plan => (
            <div key={plan._id} style={{
              background: "#fff", borderRadius: 16, padding: 24,
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
              border: `2px solid ${plan.color}20`,
              opacity: plan.isActive ? 1 : 0.6,
            }}>
              {/* Plan Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: plan.color }} />
                    <span style={{ fontSize: 18, fontWeight: 800, color: "#1b2559" }}>{plan.name}</span>
                    {plan.isDefault && (
                      <span style={{ fontSize: 10, background: "#ede9fe", color: "#4318FF", padding: "2px 8px", borderRadius: 20, fontWeight: 700 }}>DEFAULT</span>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>{plan.description}</p>
                </div>
                <span style={{
                  fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700,
                  background: plan.isActive ? "#dcfce7" : "#fef3c7",
                  color:      plan.isActive ? "#16a34a" : "#d97706",
                }}>
                  {plan.isActive ? "Active" : "Paused"}
                </span>
              </div>

              {/* Price */}
              <div style={{ background: `${plan.color}10`, borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: plan.color }}>
                  {plan.price === 0 ? "FREE" : `₹${plan.price}`}
                </span>
                {plan.price > 0 && (
                  <span style={{ fontSize: 12, color: plan.color, marginLeft: 6 }}>/{plan.billingCycle}</span>
                )}
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Valid for {plan.validityDays} days
                </div>
              </div>

              {/* Features */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#a3aed0", textTransform: "uppercase", marginBottom: 8 }}>Features</div>
                {[
                  { label: "Reports/month", value: plan.features?.maxReportsPerMonth === -1 ? "Unlimited" : plan.features?.maxReportsPerMonth },
                  { label: "Searches/day",  value: plan.features?.maxSearchesPerDay  === -1 ? "Unlimited" : plan.features?.maxSearchesPerDay  },
                  { label: "Export CSV",    value: plan.features?.canExportCSV    ? "✅" : "❌" },
                  { label: "Alerts",        value: plan.features?.canReceiveAlerts ? "✅" : "❌" },
                  { label: "Report Images", value: plan.features?.canViewReportImages ? "✅" : "❌" },
                  { label: "Bulk Import",   value: plan.features?.canBulkImport   ? "✅" : "❌" },
                ].map(f => (
                  <div key={f.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid #f4f7fe" }}>
                    <span style={{ color: "#6b7280" }}>{f.label}</span>
                    <span style={{ fontWeight: 700, color: "#1b2559" }}>{f.value}</span>
                  </div>
                ))}
              </div>

              {/* Subscribers */}
              {plan.subscriberCount !== undefined && (
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
                  👥 <b style={{ color: "#1b2559" }}>{plan.subscriberCount}</b> active subscribers
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Edit — always available */}
                <button onClick={() => openEdit(plan)}
                  style={{ flex: 1, padding: "8px 12px", background: "#ede9fe", color: "#4318FF", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                  ✏️ Edit
                </button>

                {/* Set Default — only if not already default */}
                {!plan.isDefault && plan.isActive && (
                  <button onClick={() => handleSetDefault(plan)}
                    style={{ flex: 1, padding: "8px 12px", background: "#f0fdf4", color: "#16a34a", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    ⭐ Set Default
                  </button>
                )}

                {/* Pause / Unpause — available for ALL plans including trial */}
                <button onClick={() => handleToggleActive(plan)}
                  style={{
                    flex: 1, padding: "8px 12px", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer",
                    background: plan.isActive ? "#fef3c7" : "#dcfce7",
                    color:      plan.isActive ? "#d97706" : "#16a34a",
                  }}>
                  {plan.isActive ? "⏸️ Pause" : "▶️ Activate"}
                </button>

                {/* Delete — only non-trial plans */}
                {plan.slug !== "trial" && (
                  <button onClick={() => handleDelete(plan)}
                    style={{ padding: "8px 12px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CREATE / EDIT MODAL ── */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 560, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 20px", color: "#1b2559", fontSize: 17 }}>
              {modal === "create" ? "➕ Create New Plan" : `✏️ Edit Plan — ${form.name}`}
            </h3>

            {/* Basic Info */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Plan Name *</label>
                <input style={inp} placeholder="e.g. Standard"
                  value={form.name || ""}
                  onChange={e => {
                    setF("name", e.target.value);
                    if (modal === "create") setF("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"));
                  }} />
              </div>
              <div>
                <label style={lbl}>Slug * (auto-filled)</label>
                <input style={inp} placeholder="e.g. standard"
                  value={form.slug || ""}
                  onChange={e => setF("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Description</label>
              <input style={inp} placeholder="Short description of this plan"
                value={form.description || ""} onChange={e => setF("description", e.target.value)} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Price (₹)</label>
                <input type="number" style={inp} value={form.price ?? 0}
                  onChange={e => setF("price", parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={lbl}>Billing Cycle</label>
                <select style={inp} value={form.billingCycle || "yearly"}
                  onChange={e => setF("billingCycle", e.target.value)}>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Validity (days)</label>
                <input type="number" style={inp} value={form.validityDays || 365}
                  onChange={e => setF("validityDays", parseInt(e.target.value) || 365)} />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Badge Color</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="color" value={form.color || "#4318FF"}
                    onChange={e => setF("color", e.target.value)}
                    style={{ width: 40, height: 36, border: "1.5px solid #e0e5f2", borderRadius: 8, cursor: "pointer", padding: 2 }} />
                  <input style={{ ...inp, flex: 1, marginBottom: 0 }} value={form.color || "#4318FF"}
                    onChange={e => setF("color", e.target.value)} />
                </div>
              </div>
              <div>
                <label style={lbl}>Sort Order</label>
                <input type="number" style={inp} value={form.sortOrder || 1}
                  onChange={e => setF("sortOrder", parseInt(e.target.value) || 1)} />
              </div>
            </div>

            {/* Feature Limits */}
            <div style={{ background: "#f9faff", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 12 }}>⚙️ Feature Limits</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={lbl}>Max Reports/Month (-1 = unlimited)</label>
                  <input type="number" style={inp} value={form.features?.maxReportsPerMonth ?? -1}
                    onChange={e => setFeat("maxReportsPerMonth", parseInt(e.target.value))} />
                </div>
                <div>
                  <label style={lbl}>Max Searches/Day (-1 = unlimited)</label>
                  <input type="number" style={inp} value={form.features?.maxSearchesPerDay ?? -1}
                    onChange={e => setFeat("maxSearchesPerDay", parseInt(e.target.value))} />
                </div>
              </div>

              {/* Toggle features */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { key: "canExportCSV",        label: "Export CSV"           },
                  { key: "canReceiveAlerts",    label: "Receive Alerts"       },
                  { key: "canViewReportImages", label: "View Report Images"   },
                  { key: "canBulkImport",       label: "Bulk Import"          },
                ].map(f => (
                  <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
                    <input type="checkbox"
                      checked={form.features?.[f.key] || false}
                      onChange={e => setFeat(f.key, e.target.checked)}
                      style={{ width: 16, height: 16, cursor: "pointer" }} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Status toggles */}
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
                <input type="checkbox" checked={form.isActive || false}
                  onChange={e => setF("isActive", e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                Plan is Active (visible to owners)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
                <input type="checkbox" checked={form.isDefault || false}
                  onChange={e => setF("isDefault", e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                Set as Default (for new registrations)
              </label>
            </div>

            {msg && (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontWeight: 600, fontSize: 13,
                background: msg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                color:      msg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
                {msg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setModal(null)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#4318FF", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : modal === "create" ? "Create Plan" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 };
const inp = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", marginBottom: 0, fontSize: 13, outline: "none", boxSizing: "border-box" };

export default AdminPlans;